import React, { useLayoutEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Button from "./Button";
import SinceStamp from "../components/SinceStamp";

gsap.registerPlugin(ScrollTrigger);

const LINES = ["Crafting Timeless", "Wooden Furniture"];

// Three background images for the hero section (crossfade slideshow).
// Replace the 2nd and 3rd paths with your actual images.
const HERO_BG_IMAGES = [
  "/assets/111.png",
  "/assets/112.png",
  "/assets/113.png",
];

// Seconds each background stays visible / seconds the crossfade takes
const BG_HOLD = 5;
const BG_FADE = 1.6;

// Right-side gallery images (furniture showcase)
const GALLERY_IMAGES = [
  { src: "/assets/19.png", alt: "Handcrafted wooden wardrobe" },
  { src: "/assets/26.png", alt: "Custom wooden kitchen" },
  { src: "/assets/23.png", alt: "Elegant TV unit" },
  { src: "/assets/4.png", alt: "Wooden staircase" },
];

export default function Hero() {
  const root = useRef(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      // Orchestrated intro timeline
      const tl = gsap.timeline({ defaults: { ease: "power4.out" } });

      tl.from(".hero__bg-stack", {
        scale: 1.25,
        duration: 2.2,
        ease: "power3.out",
      })
        .from(
          ".hero__meta > *",
          { y: 18, opacity: 0, duration: 0.7, stagger: 0.08 },
          0.3,
        )
        .from(
          ".hero__line > span",
          { yPercent: 115, duration: 1.15, stagger: 0.13 },
          0.45,
        )
        .from(".hero__lead", { y: 28, opacity: 0, duration: 0.9 }, 1.05)
        .from(
          ".hero .btn-row > *",
          { y: 28, opacity: 0, duration: 0.8, stagger: 0.12 },
          1.2,
        )
        .from(
          ".hero__gallery-item",
          {
            y: 60,
            opacity: 0,
            scale: 0.92,
            duration: 1.1,
            stagger: 0.15,
            ease: "power3.out",
          },
          0.6,
        )
        .from(".hero__scroll-cue", { opacity: 0, duration: 0.8 }, 1.6);

      // Background slideshow: crossfade between the three images, looping
      const slides = gsap.utils.toArray(".hero__bg-slide");
      if (slides.length > 1) {
        gsap.set(slides, { opacity: 0 });
        gsap.set(slides[0], { opacity: 1 });

        const slideTl = gsap.timeline({ repeat: -1 });
        slides.forEach((slide, i) => {
          const next = slides[(i + 1) % slides.length];
          slideTl
            .to(
              next,
              { opacity: 1, duration: BG_FADE, ease: "power1.inOut" },
              `+=${BG_HOLD}`,
            )
            .to(
              slide,
              { opacity: 0, duration: BG_FADE, ease: "power1.inOut" },
              "<",
            );
        });
      }

      // Scroll parallax
      const st = {
        trigger: root.current,
        start: "top top",
        end: "bottom top",
        scrub: true,
      };

      gsap.to(".hero__bg-stack", {
        yPercent: 15,
        scale: 1.1,
        ease: "none",
        scrollTrigger: st,
      });

      gsap.to(".hero__content", {
        yPercent: -12,
        opacity: 0,
        ease: "none",
        scrollTrigger: st,
      });

      gsap.to(".hero__gallery", {
        yPercent: -20,
        ease: "none",
        scrollTrigger: st,
      });

      gsap.to(".hero__overlay", {
        opacity: 1,
        ease: "none",
        scrollTrigger: st,
      });

      // Floating animation for gallery items (idle)
      gsap.to(".hero__gallery-item--float", {
        y: -12,
        duration: 3,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
        stagger: 0.4,
      });
    }, root);

    return () => ctx.revert();
  }, []);

  return (
    <section className="hero" ref={root}>
      <SinceStamp />

      {/* Background media: three stacked images */}
      <div className="hero__media">
        <div className="hero__bg-stack">
          {HERO_BG_IMAGES.map((src, i) => (
            <img
              key={src}
              className="hero__bg-slide"
              src={src}
              alt=""
              aria-hidden="true"
              loading={i === 0 ? "eager" : "lazy"}
            />
          ))}
        </div>
        <div className="hero__overlay" />
        <div className="hero__vignette" />
      </div>

      <div className="container hero__content">
        {/* LEFT SIDE: Text content */}
        <div className="hero__text">
          <div className="hero__meta">
            <span className="hero__eyebrow">
              Anappuram Wooden Furniture Work
            </span>
            <span className="hero__est">
              <span className="hero__est-item">Palakkad, Kerala</span>
              <span className="hero__est-dot" aria-hidden="true">
                ·
              </span>
              <span className="hero__est-item">Serving All India</span>
            </span>
          </div>

          <h1 className="hero__title">
            {LINES.map((line) => (
              <span className="hero__line" key={line}>
                <span>{line}</span>
              </span>
            ))}
            <span className="hero__line">
              <span className="text-accent">Since 1990</span>
            </span>
          </h1>

          <div className="hero__lead">
            <span className="hero__rule" aria-hidden="true" />
            <p>
              Wardrobes, kitchens, TV units, interiors and staircases,
              handcrafted in Palakkad, and delivered to customers across India.
            </p>
          </div>

          <div className="btn-row">
            <Button to="/services">Explore Services</Button>
            <Button to="/contact" variant="on-dark">
              Contact Us
            </Button>
          </div>
        </div>

        {/* RIGHT SIDE: Aesthetic furniture image gallery */}
        <div className="hero__gallery" aria-hidden="true">
          {GALLERY_IMAGES.map((img, i) => (
            <div
              className={`hero__gallery-item hero__gallery-item--${i + 1} ${
                i % 2 === 0 ? "hero__gallery-item--float" : ""
              }`}
              key={i}
            >
              <div className="hero__gallery-frame">
                <img src={img.src} alt={img.alt} />
              </div>
            </div>
          ))}
          <div className="hero__gallery-glow" />
        </div>
      </div>

      <div className="hero__scroll-cue" aria-hidden="true">
        <span />
      </div>
    </section>
  );
}
